import { cssVars } from '../utils';
import {
  ColorName,
  ColorPalette,
  PaletteColor,
  AppColors,
  CssVarDefinitions,
  DsVarOverrides,
  ColorPaletteIndex,
} from './types';
import { tokens } from './generated/tokens';

export const dsPaletteDefaults = {
  aqua: {
    50: '#DCFBFF',
    100: '#BCF7FF',
    200: '#A4F4FF',
    300: '#7EECFB',
    400: '#5DE2F4',
    500: '#3FD3E7',
    600: '#00B1C9',
    700: '#1890A1',
    800: '#0D7381',
    900: '#004F5A',
  },
  blue: {
    50: '#ECF4FF',
    100: '#D4E5FF',
    200: '#A9CBFF',
    300: '#82B4FF',
    400: '#5294F7',
    500: '#417AE9',
    600: '#2A5CBB',
    700: '#17449B',
    800: '#133880',
    900: '#192C70',
  },
  green: {
    50: '#E3FFEF',
    100: '#C9F7DC',
    200: '#A0EBC7',
    300: '#72E3AC',
    400: '#4ED996',
    500: '#26BB74',
    600: '#20A665',
    700: '#179A5B',
    800: '#14864F',
    900: '#147B4A',
  },
  grey: {
    50: '#FCFCFE',
    100: '#F4F6F9',
    200: '#E1E7EA',
    300: '#BAC3C8',
    400: '#839299',
    500: '#5D6E75',
    600: '#40545D',
    700: '#243B45',
    800: '#112934',
    900: '#051922',
  },
  indigo: {
    50: '#E6DFFF',
    100: '#C8BAFF',
    200: '#A58EFF',
    300: '#7F61F0',
    400: '#593EBE',
    500: '#473297',
    600: '#372772',
    700: '#2D2060',
    800: '#271D51',
    900: '#1A1B41',
  },
  red: {
    50: '#F9E7E7',
    100: '#EFC3C3',
    200: '#E49B9B',
    300: '#D97272',
    400: '#D15454',
    500: '#C93636',
    600: '#BC2929',
    700: '#AE1818',
    800: '#9D160D',
    900: '#8C0B03',
  },
  yellow: {
    50: '#FEF6E1',
    100: '#FCE8B5',
    200: '#FAD983',
    300: '#F8CA51',
    400: '#F7BE2C',
    500: '#F5B307',
    600: '#F4AC06',
    700: '#F0A104',
    800: '#F19617',
    900: '#EE8B02',
  },
} satisfies Record<ColorName, ColorPalette>;

export const dsPaletteVars = cssVars(
  Object.entries(dsPaletteDefaults).reduce((acc, [colorName, shades]) => {
    Object.entries(shades).forEach(([shade, hex]) => {
      acc[`${colorName}-${shade}` as PaletteColor] = hex;
    });
    return acc;
  }, {} as Record<PaletteColor, string>)
);

export const dsPaletteRefs: Record<ColorName, ColorPalette> = Object.entries(
  dsPaletteVars.refs
).reduce((acc, [colorPath, colorRef]) => {
  const [color, index] = colorPath.split('-') as [ColorName, ColorPaletteIndex];
  if (!acc[color]) {
    acc[color] = {} as ColorPalette;
  }
  acc[color][index] = colorRef;
  return acc;
}, {} as Record<ColorName, ColorPalette>);

export const dsAppColorsVars = cssVars<AppColors>({
  appBackground: tokens.theme.bg.structural.page,
});

export const dsAppColorRefs = dsAppColorsVars.refs;

export const dsVarDefinitions = {
  ...dsPaletteVars.defs,
  ...dsAppColorsVars.defs,
};

export const overrideDsVars = (vars: DsVarOverrides) => {
  const colorSetters: CssVarDefinitions = vars.colors
    ? dsAppColorsVars.setValues(vars.colors)
    : {};
  const paletteSetters: CssVarDefinitions = vars.palette
    ? dsPaletteVars.setValues(vars.palette)
    : {};
  return { ...colorSetters, ...paletteSetters };
};
